import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../utils/apiClient';
import { 
  Fingerprint, 
  Eye, 
  AlertTriangle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';

/**
 * IdentityFormationWidget
 * 
 * A lightweight entry/summary component for the Dashboard.
 * 
 * Shows:
 * - Confirmed identity elements
 * - Emerging patterns requiring attention
 * - Unresolved contradictions
 * - Recent identity evolution
 * 
 * Links to: /personal/identity-formation (the dedicated workspace)
 * 
 * UI Philosophy:
 * - "Your Twin is forming" - reassuring, not pressuring
 * - Avoid psychological/diagnostic language
 * - Mirror, not evaluator
 */

export function IdentityFormationWidget() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    try {
      const res = await apiClient.get('/identity-evidence/summary');
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch identity formation summary:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Don't show widget if there's no data at all (user hasn't started)
  if (!loading && !error && summary && 
      summary.total_evidence_count === 0 && 
      summary.confirmed_traits_count === 0) {
    return null;
  }

  if (error) {
    return null; // Silently fail - widget is optional
  }

  return (
    <Card 
      className="p-6 rounded-2xl border-2 border-indigo-100" 
      data-testid="identity-formation-widget"
    >
      <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Fingerprint className="w-5 h-5 text-indigo-600" />
          </div>
          <div className={isRTL ? 'text-right' : ''}>
            <h3 className="font-heading text-lg font-semibold text-gray-900">
              {t('identityFormation.widgetTitle', 'Your Twin is Forming')}
            </h3>
            <p className="text-sm text-gray-500">
              {t('identityFormation.widgetSubtitle', 'Identity patterns and confirmations')}
            </p>
          </div>
        </div>
        <Link to="/personal/identity-formation">
          <Button variant="outline" size="sm" className={isRTL ? 'flex-row-reverse' : ''}>
            {t('identityFormation.viewFormation', 'View Formation')}
            <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'}`} />
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : summary ? (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {/* Confirmed */}
            <div className={`p-3 bg-green-50 rounded-xl border border-green-100 ${isRTL ? 'text-right' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Fingerprint className="w-4 h-4 text-green-600" />
                <span className="text-xl font-bold text-green-700">
                  {summary.confirmed_traits_count}
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">
                {t('identityFormation.confirmedLabel', 'Confirmed')}
              </p>
            </div>

            {/* Emerging */}
            <div className={`p-3 bg-amber-50 rounded-xl border border-amber-100 ${isRTL ? 'text-right' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Eye className="w-4 h-4 text-amber-600" />
                <span className="text-xl font-bold text-amber-700">
                  {summary.emerging_patterns_count + summary.candidate_traits_count}
                </span>
              </div>
              <p className="text-xs text-amber-600 mt-1">
                {t('identityFormation.noticedLabel', 'Noticed')}
              </p>
            </div>

            {/* Uncertain */}
            <div className={`p-3 bg-red-50 rounded-xl border border-red-100 ${isRTL ? 'text-right' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-xl font-bold text-red-700">
                  {summary.active_contradictions_count}
                </span>
              </div>
              <p className="text-xs text-red-600 mt-1">
                {t('identityFormation.uncertainLabel', 'Uncertain')}
              </p>
            </div>

            {/* Evidence */}
            <div className={`p-3 bg-indigo-50 rounded-xl border border-indigo-100 ${isRTL ? 'text-right' : ''}`}>
              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <span className="text-xl font-bold text-indigo-700">
                  {summary.total_evidence_count}
                </span>
              </div>
              <p className="text-xs text-indigo-600 mt-1">
                {t('identityFormation.evidenceLabel', 'Evidence')}
              </p>
            </div>
          </div>

          {/* Recent confirmations */}
          {summary.recent_confirmations?.length > 0 && (
            <div className={`pt-3 border-t border-gray-100 ${isRTL ? 'text-right' : ''}`}>
              <p className="text-xs text-gray-500 mb-2">
                {t('identityFormation.recentlyConfirmed', 'Recently confirmed:')}
              </p>
              <div className={`flex flex-wrap gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {summary.recent_confirmations.slice(0, 3).map((trait, i) => (
                  <Badge 
                    key={i} 
                    variant="outline" 
                    className="bg-green-50 text-green-700 border-green-200"
                  >
                    {trait}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Call to action if patterns need attention */}
          {(summary.candidate_traits_count > 0 || summary.active_contradictions_count > 0) && (
            <div className={`mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100 ${isRTL ? 'text-right' : ''}`}>
              <p className="text-sm text-amber-700">
                {summary.candidate_traits_count > 0 ? (
                  t('identityFormation.patternsNeedReview', 
                    'HumaTwin noticed {{count}} pattern(s) that may be relevant to your identity.',
                    { count: summary.candidate_traits_count }
                  )
                ) : (
                  t('identityFormation.contradictionsExist',
                    'Some evidence appears mixed. Your perspective would help clarify.'
                  )
                )}
              </p>
              <Link to="/personal/identity-formation">
                <Button size="sm" variant="outline" className="mt-2 text-amber-700 border-amber-300 hover:bg-amber-100">
                  {t('identityFormation.reviewPatterns', 'Review Patterns')}
                </Button>
              </Link>
            </div>
          )}
        </>
      ) : null}
    </Card>
  );
}

export default IdentityFormationWidget;
